import React, { useState, useEffect } from 'react';
import Card from './Card';
import TextEditor from './TextEditor';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { Loader2, ScrollText, FileText, UserCheck, Eye, ArrowLeft, Check } from 'lucide-react';
import { convertMarkdownToHtml, convertHtmlToMarkdown } from './markdownConverter';
import PromptVersionList from './PromptVersionList';
import { useAuth } from '../contexts/AuthContext';

// Configuration for all prompt types
const PROMPT_TYPES = {
  prompt: {
    tabId: 'general',
    displayName: 'General Prompt',
    description: 'Main system prompt for queries',
    icon: ScrollText,
    firestoreField: 'Prompt',
    firestoreCollection: 'Prompts'
  },
  projectSpecificsPrompt: {
    tabId: 'projectSpecifics',
    displayName: 'Project Specifics',
    description: 'Project-specific context and parameters',
    icon: ScrollText,
    firestoreField: 'Project_Specifics_Prompt',
    firestoreCollection: 'Project Specifics Prompts'
  },
  businessLogicPrompt: {
    tabId: 'businessLogic',
    displayName: 'Business Logic',
    description: 'Business rules and logic for queries',
    icon: ScrollText,
    firestoreField: 'Business_Logic_Prompt',
    firestoreCollection: 'Business Logic Prompts'
  },
  explanationPrompt: {
    tabId: 'explanation',
    displayName: 'Explanation Prompt',
    description: 'Prompt for generating explanations',
    icon: FileText,
    firestoreField: 'Explanation_Prompt',
    firestoreCollection: 'Explanation Prompts'
  },
  investigateEmptyPrompt: {
    tabId: 'investigateEmpty',
    displayName: 'Investigate Empty',
    description: 'Prompt for empty result investigation',
    icon: FileText,
    firestoreField: 'Investigate_Empty_Prompt',
    firestoreCollection: 'Investigate Empty Prompts'
  },
  retryErrorPrompt: {
    tabId: 'retryError',
    displayName: 'Retry Error',
    description: 'Prompt for retry error handling',
    icon: FileText,
    firestoreField: 'Retry_Error_Prompt',
    firestoreCollection: 'Retry Error Prompts'
  },
  preferenceLearningPrompt: {
    tabId: 'preferenceLearning',
    displayName: 'Preference Learning',
    description: 'Prompt for user preference learning',
    icon: UserCheck,
    firestoreField: 'Preference_Learning_Prompt',
    firestoreCollection: 'Preference Learning Prompts'
  },
  bigBroPrompt: {
    tabId: 'bigBro',
    displayName: 'Big Bro Prompt',
    description: 'Prompt for query monitoring and suggestions',
    icon: FileText,
    firestoreField: 'Big_Bro_Prompt',
    firestoreCollection: 'Big Bro Prompts'
  },
  intentDetectionPrompt: {
    tabId: 'intentDetection',
    displayName: 'Intent Detection',
    description: 'Prompt for query intent analysis',
    icon: FileText,
    firestoreField: 'Intent_Detection_Prompt',
    firestoreCollection: 'Intent Detection Prompts'
  },
  multiTableReviewPrompt: {
    tabId: 'multiTableReview',
    displayName: 'Multi-Table Review',
    description: 'Prompt for multi-table data review',
    icon: FileText,
    firestoreField: 'Multi_Table_Review_Prompt',
    firestoreCollection: 'Multi Table Review Prompts'
  },
  sprayPrompt: {
    tabId: 'spray',
    displayName: 'Spray Review',
    description: 'Prompt for review of multi-agents SQL',
    icon: FileText,
    firestoreField: 'Spray_Prompt',
    firestoreCollection: 'Spray Prompts'
  },
  postNLQPrompt: {
    tabId: 'postNLQ',
    displayName: 'Post-NLQ',
    description: 'Prompt for post-query processing and analysis',
    icon: FileText,
    firestoreField: 'Post_NLQ_Prompt',
    firestoreCollection: 'Post NLQ Prompts'
  },
  similarQueriesPrompt: {
    tabId: 'similarQueries',
    displayName: 'Similar Queries',
    description: 'Prompt for similarity matching of validated queries',
    icon: FileText,
    firestoreField: 'SimilarQueries_Prompt',
    firestoreCollection: 'SimilarQueries Prompts'
  }
};


const PromptManagement = ({ isAIManager = false }) => {
  const [configData, setConfigData] = useState({});
  const [previewData, setPreviewData] = useState({});
  const [versions, setVersions] = useState({});
  const [previewVersions, setPreviewVersions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [activeTab, setActiveTab] = useState(isAIManager ? 'businessLogic' : 'general');
  const { currentUser } = useAuth();

  // Filter prompt types based on user role
  const getAvailablePromptTypes = () => {
    if (isAIManager) {
      // AI Managers only see Business Logic
      return {
        businessLogicPrompt: PROMPT_TYPES.businessLogicPrompt
      };
    }
    // Admins see everything
    return PROMPT_TYPES;
  };

  // Use filtered prompt types throughout the component
  const AVAILABLE_PROMPT_TYPES = getAvailablePromptTypes();

  const formatText = (text) => {
    if (!text) return '';
    const lines = text.split('\n');
    return lines
      .map(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return null;
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
          return trimmedLine;
        }
        return trimmedLine;
      })
      .filter(Boolean)
      .join('\n');
  };

  const fetchVersions = async (type) => {
    try {
      const versionsRef = collection(db, 'DBI Configuration', type, 'versions');
      const versionsQuery = query(versionsRef, orderBy('versionNumber', 'desc'));
      const versionsSnapshot = await getDocs(versionsQuery);
      
      return versionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching ${type} versions:`, error);
      return [];
    }
  };

  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        setIsLoading(true);
        const configDoc = await getDoc(doc(db, 'DBI Configuration', 'Config'));
        
        if (configDoc.exists()) {
          const configDocData = configDoc.data();
          
          // Initialize empty objects for our state
          const initialConfigData = {};
          const initialPreviewData = {};
          const initialVersions = {};

          // Fetch versions and setup data for all prompt types
          const fetchPromises = Object.entries(AVAILABLE_PROMPT_TYPES).map(async ([promptKey, promptConfig]) => {            
            const collectionVersions = await fetchVersions(promptConfig.firestoreCollection);
            initialVersions[promptKey] = collectionVersions;
            
            // Get current version content
            const currentVersionId = configDocData[promptConfig.firestoreField];
            
            // Debug logging for Post-NLQ
            if (promptKey === 'postNLQPrompt') {
              console.log('Post-NLQ Debug:');
              console.log('- Field name:', promptConfig.firestoreField);
              console.log('- Current version ID from config:', currentVersionId);
              console.log('- Available versions:', collectionVersions.map(v => ({ id: v.id, versionNumber: v.versionNumber })));
            }
            
            const currentVersion = collectionVersions.find(v => v.id === currentVersionId)?.content || '';
            
            // Set state values
            initialConfigData[promptKey] = convertMarkdownToHtml(currentVersion);
            initialConfigData[`${promptKey}Id`] = currentVersionId || null;
            initialPreviewData[promptKey] = convertMarkdownToHtml(currentVersion);
          });

          await Promise.all(fetchPromises);

          setConfigData(initialConfigData);
          setPreviewData(initialPreviewData);
          setVersions(initialVersions);
        } else {
          setError('Configuration data not found');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching config data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigData();
  }, []);

  const handleVersionSelect = (type, version) => {
    // Update preview content without changing the active version
    setPreviewData(prev => ({
      ...prev,
      [type]: convertMarkdownToHtml(version.content)
    }));
    
    // Store the preview version information
    setPreviewVersions(prev => ({
      ...prev,
      [type]: version
    }));
  };
  
  const handleVersionApply = async (type, version) => {
    try {
      setSaveStatus({ type: 'loading', message: 'Applying version...' });
      
      const promptConfig = AVAILABLE_PROMPT_TYPES[type];

      // Update Config document to point to the selected version
      const configRef = doc(db, 'DBI Configuration', 'Config');
      await updateDoc(configRef, {
        [promptConfig.firestoreField]: version.id
      });
  
      // Update the version document to track who applied it and when
      const versionRef = doc(db, 'DBI Configuration', promptConfig.firestoreCollection, 'versions', version.id);
      await updateDoc(versionRef, {
        lastAppliedAt: serverTimestamp(),
        lastAppliedBy: currentUser?.email || 'unknown'
      });
    
      // Update both config and preview data
      const newContent = convertMarkdownToHtml(version.content);
      setConfigData(prev => ({
        ...prev,
        [type]: newContent,
        [`${type}Id`]: version.id
      }));
      setPreviewData(prev => ({
        ...prev,
        [type]: newContent
      }));
  
      // Update versions state to reflect the new applied timestamp
      setVersions(prev => ({
        ...prev,
        [type]: prev[type].map(v => 
          v.id === version.id 
            ? { 
                ...v, 
                lastAppliedAt: new Date(),
                lastAppliedBy: currentUser?.email || 'unknown'
              } 
            : v
        )
      }));
      
      // Clear the preview version after applying
      setPreviewVersions(prev => ({
        ...prev,
        [type]: null
      }));
  
      setSaveStatus({ type: 'success', message: `Applied version ${version.versionNumber}` });
    } catch (error) {
      console.error('Error applying version:', error);
      setSaveStatus({ type: 'error', message: 'Failed to apply version' });
    } finally {
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    }
  };

  const handleSave = async (type, content) => {
    try {
      setSaveStatus({ type: 'loading', message: 'Saving new version...' });
      
      const promptConfig = AVAILABLE_PROMPT_TYPES[type];      
      const markdownContent = convertHtmlToMarkdown(content);
      
      // Get the next version number
      const currentVersions = versions[type] || [];
      const nextVersionNumber = currentVersions.length > 0 ? 
        Math.max(...currentVersions.map(v => v.versionNumber || 0)) + 1 : 1;
  
      // Create new version document
      const versionsRef = collection(db, 'DBI Configuration', promptConfig.firestoreCollection, 'versions');
      const newVersion = {
        content: markdownContent,
        createdAt: serverTimestamp(),
        createdBy: currentUser?.email || 'unknown',
        versionNumber: nextVersionNumber,
        lastAppliedAt: serverTimestamp(),
        lastAppliedBy: currentUser?.email || 'unknown'
      };
  
      const newVersionRef = await addDoc(versionsRef, newVersion);
  
      // Update Config document to point to new version
      const configRef = doc(db, 'DBI Configuration', 'Config');
      await updateDoc(configRef, {
        [promptConfig.firestoreField]: newVersionRef.id
      });
  
      // Update local state with the new version
      const newVersionWithId = {
        id: newVersionRef.id,
        ...newVersion,
        createdAt: new Date(),
        lastAppliedAt: new Date()
      };
  
      setVersions(prev => ({
        ...prev,
        [type]: [newVersionWithId, ...(prev[type] || [])]
      }));
  
      // Update both config and preview data
      setConfigData(prev => ({
        ...prev,
        [type]: content,
        [`${type}Id`]: newVersionRef.id
      }));
      setPreviewData(prev => ({
        ...prev,
        [type]: content
      }));
      
      // Clear any preview version
      setPreviewVersions(prev => ({
        ...prev,
        [type]: null
      }));
  
      setSaveStatus({ type: 'success', message: 'Saved and activated new version!' });
    } catch (err) {
      console.error('Error saving:', err);
      setSaveStatus({ 
        type: 'error', 
        message: `Failed to save changes: ${err.message}` 
      });
    } finally {
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    }
  };

  const handleCancel = (type) => {
    // Reset preview to current active version
    const currentVersion = versions[type]?.find(v => v.id === configData[`${type}Id`]);
    if (currentVersion) {
      setPreviewData(prev => ({
        ...prev,
        [type]: convertMarkdownToHtml(currentVersion.content)
      }));
      
      // Clear the preview version
      setPreviewVersions(prev => ({
        ...prev,
        [type]: null
      }));
    }
    setSaveStatus({ type: '', message: '' });
  };

  const handleCommentEdit = async (type, versionId, comment) => {
    try {
      setSaveStatus({ type: 'loading', message: 'Saving comment...' });
      
      const promptConfig = AVAILABLE_PROMPT_TYPES[type];
            
      // Update the version document
      const versionRef = doc(db, 'DBI Configuration', promptConfig.firestoreCollection, 'versions', versionId);
      await updateDoc(versionRef, {
        comment: comment
      });
  
      // Update local state
      setVersions(prev => ({
        ...prev,
        [type]: prev[type].map(v => 
          v.id === versionId 
            ? { ...v, comment } 
            : v
        )
      }));
      
      // Update preview version if it's the one being edited
      if (previewVersions[type]?.id === versionId) {
        setPreviewVersions(prev => ({
          ...prev,
          [type]: { ...prev[type], comment }
        }));
      }
  
      setSaveStatus({ type: 'success', message: 'Comment saved successfully' });
    } catch (error) {
      console.error('Error saving comment:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save comment' });
    } finally {
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    }
  };

  // Determine which prompt type is currently active
  const getActivePromptType = () => {
    return Object.entries(AVAILABLE_PROMPT_TYPES).find(([_, config]) => config.tabId === activeTab)?.[0];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading configuration: {error}
      </div>
    );
  }

  // Components for rendering tab buttons and content
  const TabButton = ({ promptType, promptConfig }) => {
    const Icon = promptConfig.icon;
    const isActive = activeTab === promptConfig.tabId;
    
    return (
      <button
        onClick={() => setActiveTab(promptConfig.tabId)}
        className={`w-full flex items-center gap-2 px-3 py-3 text-left transition-colors ${
          isActive 
            ? 'bg-primary/5 border-r-4 border-primary'
            : 'hover:bg-gray-50'
        }`}
      >
        <Icon className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-gray-500'}`} />
        <div>
          <div className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-gray-700'}`}>
            {promptConfig.displayName}
          </div>
          <div className="text-[11px] text-gray-500">
            {promptConfig.description}
          </div>
        </div>
      </button>
    );
  };
  
  const TabContent = ({ promptType }) => {
    const activePromptType = promptType;
    const currentVersionId = configData[`${activePromptType}Id`];
    const previewVersion = previewVersions[activePromptType];
    const isPreviewMode = previewVersion && previewVersion.id !== currentVersionId;
    const currentVersionData = versions[activePromptType]?.find(v => v.id === currentVersionId);
    
    const formatDate = (timestamp) => {
      if (!timestamp) return 'Unknown date';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    return (
      <div className={`h-full flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden ${isPreviewMode ? 'ring-2 ring-blue-200' : ''}`}>
        <div className="px-4 pt-4 pb-2 border-b flex-shrink-0 bg-white">
          <PromptVersionList
            versions={versions[activePromptType] || []}
            currentVersion={currentVersionId}
            onVersionSelect={(version) => handleVersionSelect(activePromptType, version)}
            onVersionApply={(version) => handleVersionApply(activePromptType, version)}
            onCommentEdit={(versionId, comment) => handleCommentEdit(activePromptType, versionId, comment)}
            isLoading={isLoading}
          />
        </div>
        
        {/* Preview Mode Indicator */}
        {isPreviewMode && (
          <div className="border-t border-b border-blue-200 bg-blue-50/80 p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-blue-700 flex items-center gap-2">
                <Eye className="h-4.5 w-4.5 text-blue-600" />
                <span>Previewing Version {previewVersion.versionNumber}</span>
              </div>
            </div>
            
            <div className="text-xs text-gray-600">
              Created by{' '}
              <span className="bg-gray-100 px-1.5 py-0.5 rounded">{previewVersion?.createdBy}</span>
              {' on '}
              <span className="text-gray-500">{formatDate(previewVersion?.createdAt)}</span>
            </div>

            {previewVersion?.comment && (
              <div className="mt-2 text-sm font-semibold text-primary">
                {previewVersion.comment}
              </div>
            )}
            
            <div className="flex justify-between mt-3">
              <button 
                onClick={() => handleVersionSelect(activePromptType, currentVersionData)}
                className="text-sm px-3.5 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-md flex items-center gap-2 group transition-all shadow-sm hover:shadow"
              >
                <ArrowLeft className="h-4 w-4 text-gray-600 group-hover:text-gray-700" />
                <span>Return to Active Version</span>
              </button>

              <button
                onClick={() => handleApplyClick(activePromptType, previewVersion)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
              >
                <Check className="h-4 w-4" />
                <span>Apply Version {previewVersion.versionNumber}</span>
              </button>
            </div>
          </div>
        )}
        
        <div className="flex-1 p-4 min-h-0 overflow-y-auto">
          <TextEditor
            title=""
            initialContent={formatText(previewData[activePromptType])}
            onSave={(content) => handleSave(activePromptType, content)}
            onCancel={() => handleCancel(activePromptType)}
            height="h-full"
          />
        </div>
      </div>
    );
  };
  
  const handleApplyClick = (type, version) => {
    handleVersionApply(type, version);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        {saveStatus.message && (
          <div className={`px-4 py-2 rounded ${
            saveStatus.type === 'success' ? 'bg-green-100 text-green-700' :
            saveStatus.type === 'error' ? 'bg-red-100 text-red-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {saveStatus.message}
          </div>
        )}
      </div>
      
      <div className="flex gap-4 h-[calc(100vh-8rem)]">
        {/* Vertical Tabs */}
        <div className="w-52 flex-shrink-0 h-full">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full overflow-y-auto">
          {Object.entries(AVAILABLE_PROMPT_TYPES).map(([promptType, promptConfig]) => (
              <TabButton 
                key={promptType}
                promptType={promptType}
                promptConfig={promptConfig}
              />
            ))}
          </div>
        </div>
  
        {/* Content Area */}
        <div className="flex-1 h-full overflow-hidden">
          <TabContent promptType={getActivePromptType()} />
        </div>
      </div>
    </div>
  );
};

export default PromptManagement;